/* eslint-disable @typescript-eslint/naming-convention */
import { CreateToastFnReturn, useToast } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'

import { getPlayerDetailUrl } from '@common/utils'

import { baseStatsApiURL, superscoreBaseWebURL } from '../common/config'
import { useUser } from '../queries/useUser'

async function fetchData(toast?: CreateToastFnReturn, term?: string, accessToken?: string) {
    if (!accessToken) {
        throw new Error('No access token provided')
    }

    const response = await fetch(`${baseStatsApiURL}/search/v3/query/brsuperbetsport/en-GB`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            term,
            filters: {
                entity_type: 'player',
                sport_id: 'soccer',
            },
            size: 50,
        }),
    })

    const json = await response.json()

    if (response.status !== 200) {
        toast?.({
            position: 'top',
            description: 'Error fetching players list!',
            status: 'error',
            duration: 9000,
            isClosable: true,
        })

        throw new Error(json?.message ?? json?.error ?? '¯\\_(ツ)_/¯')
    }

    return json?.results
}

function transformPlayer(player) {
    return {
        ...player,
        shirt_number: player.shirt_number?.value ? { value: Number(player.shirt_number.value) } : null,
        playerDetailUrl:
            player.seo_id?.hash?.value && player.seo_id?.slug?.value
                ? getPlayerDetailUrl(superscoreBaseWebURL, player.seo_id.slug?.value, player.seo_id.hash?.value)
                : null,
    }
}

function getFilteredPlayers(data) {
    if (!data) {
        return []
    }

    return data.flatMap(item => {
        if (item.sport !== 'soccer' || !item.content?.player) {
            return []
        }

        return [transformPlayer(item.content.player)]
    })
}

export function usePlayerSearch(searchTerm = '') {
    const { userData } = useUser()

    const toast = useToast()

    const {
        data: players,
        isError,
        isLoading,
    } = useQuery({
        queryKey: ['playersSearch', toast, searchTerm, userData.accessToken],
        queryFn: () => fetchData(toast, searchTerm, userData.accessToken),
        select: getFilteredPlayers,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        enabled: searchTerm.length > 2,
    })

    return { players, isLoading, isError }
}
